import {
  faCircleCheck,
  faCircleXmark,
  faClock,
  faWarning,
  faSpinner,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "src/base-components/Icon";

type Props = {
  statusCode: string;
  isCancelling?: boolean;
};

type DecisiontStatus = "success" | "pending" | "error";
export const getDecisionStatus = (statusCode: string): DecisiontStatus => {
  if (statusCode === "200") return "success";
  if (statusCode === "202") return "pending";
  return "error";
};

export type DecisionStatusCode =
  (typeof POSSIBLE_DECISION_STATUS_CODES)[number]["key"];

export const POSSIBLE_DECISION_STATUS_CODES = [
  { key: "200", value: "Successful" },
  { key: "202", value: "Pending" },
  { key: "408", value: "Failed: timeout (408)" },
  { key: "409", value: "Failed: output schema (409)" },
  { key: "410", value: "Canceled" },
  { key: "422", value: "Failed: input schema (422)" },
  { key: "424", value: "Failed: logic (424)" },
  { key: "500", value: "Failed: system (500)" },
  { key: "502", value: "Failed: integration system (502)" },
] as const;

const StatusMessage: React.FC<{ statusCode: string }> = ({ statusCode }) => {
  return (
    <span className="capitalize">
      {
        POSSIBLE_DECISION_STATUS_CODES.find((entry) => entry.key === statusCode)
          ?.value
      }
    </span>
  );
};

/**
 * Decision history status column shared between flow level and version level tables.
 */
export const SharedDecisionHistoryStatus: React.FC<Props> = ({
  statusCode,
  isCancelling,
}) => {
  if (isCancelling && statusCode === "202") {
    return (
      <div className="flex items-center gap-1 whitespace-nowrap">
        <Icon color="text-gray-500" icon={faSpinner} size="xs" />
        <span className="text-gray-600">Cancelling...</span>
      </div>
    );
  }
  if (statusCode === "200") {
    return (
      <div className="flex items-center gap-1 whitespace-nowrap">
        <Icon color="text-green-500" icon={faCircleCheck} size="xs" />
        <StatusMessage statusCode={statusCode} />
      </div>
    );
  }
  if (statusCode === "410") {
    return (
      <div className="flex items-center gap-1 whitespace-nowrap">
        <Icon color="text-orange-500" icon={faCircleXmark} size="xs" />
        <StatusMessage statusCode={statusCode} />
      </div>
    );
  }
  if (statusCode === "202") {
    return (
      <div className="flex items-center gap-1 whitespace-nowrap">
        <Icon color="text-indigo-400" icon={faClock} size="xs" />
        <StatusMessage statusCode={statusCode} />
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 whitespace-nowrap">
      <Icon color="text-red-600" icon={faWarning} size="xs" />
      <span>
        {POSSIBLE_DECISION_STATUS_CODES.find(
          (entry) => entry.key === statusCode,
        )?.value ?? `Error (${statusCode})`}
      </span>
    </div>
  );
};
